import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LayoutDashboard, BookOpen, Timer, ChevronLeft } from 'lucide-react'
import { signOut } from 'supertokens-web-js/recipe/emailpassword'
import { useUser } from '../context/UserContext'
import { PrimaryButton } from './ui/PrimaryButton'
import { COLORS } from '@renderer/config/colors'
import logoImg from '../assets/AurilearnLogo.png'

const isSessionRoute = (path: string): boolean =>
  /^\/exercise\/[^/]+\/chat/.test(path) || /^\/lab\/[^/]+/.test(path)

const HEADER_H = '78px'

export const Sidebar: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useUser()
  const [isCollapsed, setIsCollapsed] = useState(false)

  const onSessionRoute = isSessionRoute(location.pathname)

  useEffect(() => {
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setIsCollapsed(onSessionRoute))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [onSessionRoute])

  const handleCollapse = () => setIsCollapsed((prev) => !prev)

  const handleLogout = async (): Promise<void> => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const railCollapsed = isCollapsed
  const coursesActive =
    location.pathname === '/main' ||
    location.pathname.startsWith('/course/') ||
    location.pathname.startsWith('/exercise/')
  const userInitial = user?.email?.charAt(0).toUpperCase() || 'N'

  return (
    <aside
      aria-label="Main navigation"
      className="h-full flex-shrink-0 overflow-hidden border-r bg-white transition-[width] duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
      style={{ width: railCollapsed ? '4rem' : '16rem', borderColor: COLORS.border }}
    >
      <div className="flex h-full w-64 flex-col bg-white">
        <div
          className="flex flex-shrink-0 items-center px-2"
          style={{ height: HEADER_H }}
        >
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            aria-label="Aurilearn home"
            title={railCollapsed ? 'Aurilearn home' : undefined}
            className="flex items-center gap-3 h-11 rounded-xl cursor-pointer bg-transparent border-none w-[var(--brand-w)] transition-[width] duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ '--brand-w': railCollapsed ? '2.75rem' : '15rem' } as React.CSSProperties}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: railCollapsed ? '2.75rem' : '3rem',
                height: railCollapsed ? '2.75rem' : '3rem',
                transition:
                  'width 700ms cubic-bezier(0.4, 0, 0.2, 1), height 700ms cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <img src={logoImg} alt="" className="w-full h-full object-contain" />
            </div>
            <span
              aria-hidden="true"
              className="text-2xl leading-8 font-bold whitespace-nowrap overflow-hidden"
              style={{
                color: '#3a2e1a',
                opacity: railCollapsed ? 0 : 1,
                transform: railCollapsed ? 'translateX(-6px)' : 'translateX(0)',
                transition: railCollapsed
                  ? 'opacity 200ms ease-in, transform 200ms ease-in'
                  : 'opacity 400ms 350ms ease-out, transform 400ms 350ms ease-out'
              }}
            >
              Aurilearn
            </span>
          </button>
        </div>

        <nav
          aria-label="Primary"
          className="flex flex-col gap-2 flex-1 overflow-y-auto overflow-x-hidden px-2 py-4"
        >
          <PrimaryButton
            icon={<LayoutDashboard size={20} strokeWidth={2} />}
            label="Dashboard"
            onClick={() => navigate('/dashboard')}
            isActive={location.pathname === '/dashboard'}
            isCollapsed={railCollapsed}
          />
          <PrimaryButton
            icon={<BookOpen size={20} strokeWidth={2} />}
            label="Courses"
            onClick={() => navigate('/main')}
            isActive={coursesActive}
            isCollapsed={railCollapsed}
          />
          <PrimaryButton
            icon={<Timer size={20} strokeWidth={2} />}
            label="Sessions"
            onClick={() => navigate('/sessions')}
            isActive={location.pathname === '/sessions'}
            isCollapsed={railCollapsed}
          />
        </nav>

        <div className="my-4 mx-2 border-t" style={{ borderColor: COLORS.border }} />

        <div className="flex flex-col gap-2 pb-4 px-2">
          <PrimaryButton
            icon={
              <ChevronLeft
                size={20}
                strokeWidth={2}
                style={{
                  transform: railCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
            }
            label="Collapse"
            ariaLabel={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            ariaExpanded={!railCollapsed}
            onClick={handleCollapse}
            isActive={false}
            isCollapsed={railCollapsed}
          />
          <PrimaryButton
            icon={
              <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center">
                <span className="text-white text-[10px] font-semibold">{userInitial}</span>
              </div>
            }
            label="Logout"
            onClick={handleLogout}
            isActive={false}
            isCollapsed={railCollapsed}
            className="[&>span]:text-[#e05c3a]"
          />
        </div>
      </div>
    </aside>
  )
}
